import Point from "@arcgis/core/geometry/Point";
import * as equalsOperator from "@arcgis/core/geometry/operators/equalsOperator.js";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store/storeSetup";
import { Popover } from "react-tiny-popover";
import { AppConfig, LayersConfig } from "../../AppConfig";
import CloseIcon from "../../assets/close.svg?react";
import DownloadIcon from "../../assets/download.svg?react";
import LightBulbIcon from "../../assets/lightCalculation/lightBulb_outline.svg?react";
import InfoIcon from "../../assets/info.svg?react";
import OptionsIcon from "../../assets/options.svg?react";
import SectorLinesIcon from "../../assets/sectorLines.svg?react";
import { JobsStatus, JobsType } from "../../constants/Jobs";
import { LeftPanelTabs, RightPanelTabs } from "../../constants/Panels";
import { AppContext, JobContext, LighthouseContext, UserContext, VersionContext } from "../../Context";
import {
    changeRightPanelTab,
    setIsOpeningTool,
    setLeftPanelActiveTab,
    setLightCalculatorOpen,
    setLoading,
    setRightPanelActiveTab,
} from "../../store/appSlice";
import {
    setCurrentLighthouseTrueNorth,
    setIsDrawing,
    setIsEditing,
    setIsSavingLighthouse,
    setIsSectorEditorOpen,
    setIsSectorTableOpen,
} from "../../store/lighthouseSlice";
import { StoreState } from "../../store/rootReducer";
import {
    computeTrueNorth,
    createRelatedFeatures,
    exportFeatureToShape,
    GetCodedValueAndAlias,
    getMapView,
    getMainRelatedFeature,
    highlightFeature,
    isPairedSecondaryFeature,
    Tool,
} from "../../utils/arcgisUtils";
import { updateFDVObjectNew } from "../../utils/FDVUtils";
import {
    addObjectToJobSimple,
    createNewJob,
    createNewJobStep,
    discontinueOrRemoveNauticalObject,
    getJobByGlobalId,
    getJobs,
    getOpenJobsForObject,
    replaceNauticalObject,
} from "../../utils/jobsUtils";
import { resolveKystverketRegionAndRode } from "../../utils/autofillUtils";
import { copyLightCalculationValues } from "../../utils/lightCalculationTransfer";
import { LighthouseData } from "../../utils/lighthouseConstants";
import {
    canHaveSectors,
    isLighthouse,
    loadLighthouse,
    removeLighthouseEditingOverlay,
    saveLighthouse,
    unhideLighthouses,
} from "../../utils/lighthouseUtils";
import { getStatusFieldName, NFSStatus, setNewStatusToFeature } from "../../utils/nfsUtils";
import { hasAttachedOverettLinje } from "../../utils/overettUtils";
import {
    canEditVersion,
    checkIfInsert,
    deleteIfOnlyInVersion,
    getDefaultAndOperativeFeature,
    isDefaultVersion,
} from "../../utils/versioningUtils";
import "../FeatureViewer/FeatureViewer.css";
import FeatureViewerContent from "../FeatureViewer/FeatureViewerContent";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { featureHasLight, layerAllowsLightCalc } from "../LightCalculator/lightCalculationUtils";
import AllObjectTypesPanel from "../MapTools/CreateObject/AllObjectTypesPanel";
import type { ReplaceOptions } from "../MapTools/CreateObject/ObjectTypeButton";
import { drawSectors } from "../MapTools/LightHouse/lighthouseDrawing";
import OverettButton from "../MapTools/Overett/OverettButton";
import "./FeatureEditor.css";
import { deleteHelperpoint, postHelperPointChangeToPlanRoot } from "../../utils/newVersioningUtils";
import {
    computeChangedFieldsVsDefault,
    evaluateFastPath,
    fastPathReasonText,
    FieldChange,
    FastPathSideInput,
    SECONDARY_ONLY_IGNORED_FIELDS,
} from "../../utils/directToDefaultUtils";
import {
    FastPathPair,
    fastPathConfigOptions,
    resolveFastPathPair,
    runFastPath,
    detectRelatedSectorChanges,
} from "../../utils/fastPathUtils";
import { toast } from "react-toastify";
import Loader from "../genericComponents/Loader/Loader";
import { FeatureClasses, NauticalClasses } from "../../constants/NauticalObjects";
import ScreeningReportModal from "../FeatureViewer/ScreeningReportModal/ScreeningReportModal";
import RestoreObjectModal from "../FeatureViewer/RestoreObjectModal/RestoreObjectModal";
import ConfirmationPopover from "../genericComponents/confirmationPopover/confirmationPopover";
import ExistingJobsPopover from "./ExistingJobsPopover";

// Child layers/tables that only exist as part of a primary object (Lys, Fastsjømerke, Flytendemerke, ...)
const NON_PRIMARY_LAYER_IDS: ReadonlySet<number> = new Set<number>([
    FeatureClasses.Sektorlinje,
    FeatureClasses.Sektorflate,
    FeatureClasses.Fundament,
    FeatureClasses.KjettingInfo,
    FeatureClasses.LysInfo,
    FeatureClasses.Lyssektor,
    FeatureClasses.Skjerming,
    FeatureClasses.StakeInfo,
    FeatureClasses.CandelaCL,
    FeatureClasses.KarakterCL,
]);

// Non-primary layers, plus IB/Flytendemerke paired with a Lys
const isNonPrimaryFeature = (feature: Graphic): boolean => {
    const layerId = (feature.layer as FeatureLayer | undefined)?.layerId;
    if (layerId !== undefined && layerId !== null && NON_PRIMARY_LAYER_IDS.has(layerId)) return true;
    return isPairedSecondaryFeature(feature);
};

const FeatureEditor = () => {
    const dispatch = useDispatch<AppDispatch>();

    const appContext = useContext(AppContext);
    const lighthouseContext = useContext(LighthouseContext);
    const userContext = useContext(UserContext);
    const versionContext = useContext(VersionContext);
    const jobContext = useContext(JobContext);
    const selectedFeature = appContext?.selectedFeature.value;
    const setSelectedFeature = appContext?.selectedFeature.set;
    const setShownFeature = appContext?.shownFeature.set;
    const userName = userContext?.user.value?.fullName;
    const selectedFeatureHighlightRef = appContext?.selectedFeatureHighlightRef.value;

    const [canBeSentToFdv, setCanBeSentToFdv] = useState<boolean>(false);
    const [isFDVSending, setIsFDVSending] = useState<boolean>(false);

    const [canIEditVersion, setCanIEditVersion] = useState<boolean>(false);
    const [optionsPopoverOpen, setOptionsPopoverOpen] = useState<boolean>(false);
    const [isCheckingExistingJobs, setIsCheckingExistingJobs] = useState<boolean>(false);
    const [existingJobs, setExistingJobs] = useState<Graphic[]>([]);
    // The main feature the existing-jobs lookup ran for, so "Opprett ny jobb" creates the job on it.
    const existingJobsFeatureRef = useRef<Graphic | null>(null);
    const [isReplacing, setIsReplacing] = useState<boolean>(false);
    const [isPartOfOverett, setIsPartOfOverett] = useState<boolean>(false);
    const [canMove, setCanMove] = useState<boolean>(false);
    const [isSendingHelperPoint, setIsSendingHelperPoint] = useState<boolean>(false);
    const [isDeletingHelperPoint, setIsDeletingHelperPoint] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);

    const [isScreenReportModalOpen, setIsScreenReportModalOpen] = useState<boolean>(false);
    const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<string[]>([]);

    const [restorableObjects, setRestorableObjects] = useState<{
        defaultFeature: Graphic | null;
        operativeFeature: Graphic | null;
    }>({ defaultFeature: null, operativeFeature: null });
    const [isLoadingRestorableObjects, setIsLoadingRestorableObjects] = useState<boolean>(false);
    const [isRestoreModalOpen, setIsRestoreModalOpen] = useState<boolean>(false);
    const [allowDelete, setAllowDelete] = useState<boolean>(false);
    const [hasEditedAttributes, setHasEditedAttributes] = useState<boolean>(false);

    const [isFastPathBusy, setIsFastPathBusy] = useState<boolean>(false);
    // Fast-path pair: `primary` is always the Lys-side feature when a pair is involved;
    // when the user has selected the secondary directly, `primary` is the resolved Lys
    // partner. Tracked here so the button can gate on BOTH sides and we can fetch the
    // DEFAULT snapshots needed to compute the diff. runFastPath re-resolves independently
    // — these slots are for the button-display gate only.
    const [fastPathPair, setFastPathPair] = useState<FastPathPair | null>(null);
    const [fastPathPrimaryDefault, setFastPathPrimaryDefault] = useState<Graphic | null>(null);
    const [fastPathSecondaryDefault, setFastPathSecondaryDefault] = useState<Graphic | null>(null);
    const [fastPathPrimaryChanges, setFastPathPrimaryChanges] = useState<FieldChange[]>([]);
    const [fastPathPrimaryGeometryChanged, setFastPathPrimaryGeometryChanged] = useState<boolean>(false);
    const [fastPathSecondaryChanges, setFastPathSecondaryChanges] = useState<FieldChange[]>([]);
    const [fastPathSecondaryGeometryChanged, setFastPathSecondaryGeometryChanged] = useState<boolean>(false);
    const [fastPathInfoOpen, setFastPathInfoOpen] = useState<boolean>(false);
    const [fastPathRelatedSectorChanged, setFastPathRelatedSectorChanged] = useState<boolean>(false);

    const mapScale = useSelector((state: StoreState) => state.app.mapScale);
    const lighthouseTableSelectedSector = useSelector(
        (state: StoreState) => state.lighthouse.lighthouseTableSelectedSector
    );
    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const trueNorth = useSelector((state: StoreState) => state.lighthouse.currentLighthouseTrueNorth);
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const activeGuid = useSelector((state: StoreState) => state.version.activeGuid);
    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);
    const isSavingLighthouse = useSelector((state: StoreState) => state.lighthouse.isSavingLighthouse);
    const versionInfo = useSelector((state: StoreState) => state.version.versionInfo);
    const showSectorColors = useSelector((state: StoreState) => state.lighthouse.showSectorColors);
    const sectorOpacity = useSelector((state: StoreState) => state.lighthouse.sectorOpacity);
    const showRemovedFeatures = useSelector((state: StoreState) => state.app.showRemovedFeatures);

    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const layerConfig = useRef<LayersConfig | null | undefined>(null);
    // Updating the selected feature will trigger a re-render
    // Therefore, we need to keep track of the last selected feature
    // to avoid exiting edit mode when the feature is updated
    const lastSelectedFeatureRef = useRef<Graphic | null | undefined>(null);

    const setLighthouseData = (data: LighthouseData) => {
        lighthouseContext?.lighthouseData.set(data);
        lighthouseContext!.lighthouseDataRef.value.current = data;
    };

    const drawNewLighthouse = () => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        const newFeature = appContext?.selectedFeature.value;
        if (!newFeature) return;

        const lighthouseLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
        if (!lighthouseLayer) return;

        computeTrueNorth(selectedFeature?.attributes.lighthouseData.center).then((angle) => {
            dispatch(setCurrentLighthouseTrueNorth(angle));
            drawSectors({
                graphicsLayer: lighthouseLayer,
                data: newFeature.attributes.lighthouseData,
                setIsEditing: (value) => dispatch(setIsEditing(value)),
                sectorsToFill: [],
                opacity: sectorOpacity,
                drawNumbers: false,
                trueNorthCorrection: angle,
                centerFeature: selectedFeature,
            });
        });
    };

    const openSeaCableEditing = () => {
        if (selectedFeature) {
            const result = dispatch(changeRightPanelTab(RightPanelTabs.SEA_CABLE));
            if (!result) {
                // User cancelled, don't unselect
                return;
            }
        }
    };

    const openSectorEditing = async () => {
        if (selectedFeature) {
            if (hasUnsavedChanges) {
                toast.warning("Fullfør flytting før du bytter verktøy.");
                return;
            }
            await loadLighthouse(
                selectedFeature.attributes.objectid,
                selectedFeature.layer as FeatureLayer,
                lighthouseContext?.lighthouseData.set,
                appContext?.selectedFeature.set,
                (loadingStatus: boolean) => dispatch(setLoading(loadingStatus)),
                () => {},
                selectedFeature
            );
            const trueNorth = await computeTrueNorth(selectedFeature.geometry as Point);
            dispatch(setCurrentLighthouseTrueNorth(trueNorth));
            dispatch(setIsSectorEditorOpen(true));
            dispatch(setRightPanelActiveTab(RightPanelTabs.SECTOR));
        }
        removeLighthouseEditingOverlay();
    };

    const unselectFeature = () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.UNDEFINED));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }

        setSelectedFeature?.(null);
        lastSelectedFeatureRef.current = null;

        if (!isSavingLighthouse) {
            const mapview = getMapView();
            if (!mapview?.map) return;

            unhideLighthouses(showRemovedFeatures);

            const lighthouseEditingLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
            lighthouseEditingLayer?.removeAll();
        }
    };

    const openLightCalculation = () => {
        if (selectedFeature) {
            const result = dispatch(changeRightPanelTab(RightPanelTabs.LIGHT_CALCULATOR));
            if (!result) {
                // User cancelled, don't open
                return;
            }

            dispatch(setIsOpeningTool(Tool.LIGHT_CALCULATOR));
            dispatch(setLightCalculatorOpen({ open: true, loadFeatureValues: true }));
        }
    };

    const handleSelectFeature = async () => {
        const mapview = getMapView();
        if (lastSelectedFeatureRef.current?.layer) {
            await lastSelectedFeatureRef.current?.layer?.load();
            await mapview?.whenLayerView(lastSelectedFeatureRef.current?.layer as FeatureLayer);
        }
        selectedFeatureHighlightRef?.current?.remove();

        lastSelectedFeatureRef.current = selectedFeature;
        if (selectedFeature) {
            if (selectedFeature.attributes.lighthouseData) {
                lighthouseContext?.lighthouseSectorPanelsOpen.set(new Set());
                drawNewLighthouse();
            } else {
                const lighthouseEditingLayer = mapview?.map?.findLayerById("FyrlyktEditing") as GraphicsLayer;
                lighthouseEditingLayer?.removeAll();
            }
            const handle = await highlightFeature(selectedFeature);
            if (handle) {
                selectedFeatureHighlightRef!.current = handle;
            }

            layerConfig.current = AppConfig.Layers.find(
                (layerConfig) => layerConfig.Name === selectedFeature.layer?.id
            );
        }

        dispatch(setIsSectorEditorOpen(false));
        dispatch(setIsSectorTableOpen(false));
        if (!mapview?.map) return;

        unhideLighthouses(showRemovedFeatures);

        const lighthouseEditingLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
        await lighthouseEditingLayer.load();
        lighthouseEditingLayer.removeAll();

        return () => {
            if (selectedFeatureHighlightRef?.current) {
                selectedFeatureHighlightRef.current.remove();
            }
        };
    };

    useEffect(() => {
        handleSelectFeature();
    }, [selectedFeature]);

    useEffect(() => {
        const isOverettLinje = () => {
            if (!selectedFeature) return false;
            return [FeatureClasses.Overettlinjelys, FeatureClasses.Overettlinjemerke].includes(
                (selectedFeature.layer as FeatureLayer).layerId as number
            );
        };

        setIsPartOfOverett(false);
        setCanMove(false);
        if (!selectedFeature) return;

        if (isOverettLinje()) {
            setIsPartOfOverett(true);
            setCanMove(false);
            return;
        }

        hasAttachedOverettLinje(selectedFeature).then((result) => {
            setIsPartOfOverett(result);
            // Moving a non-primary object on its own would split it from its primary
            setCanMove(!result && !isNonPrimaryFeature(selectedFeature));
        });
    }, [selectedFeature]);

    useEffect(() => {
        if (rightPanelActiveTab !== RightPanelTabs.EDITOR) {
            selectedFeatureHighlightRef?.current?.remove();
        }
        if (rightPanelActiveTab === RightPanelTabs.EDITOR && selectedFeature) {
            highlightFeature(selectedFeature).then((handle) => {
                if (handle) {
                    selectedFeatureHighlightRef?.current?.remove();
                    selectedFeatureHighlightRef!.current = handle;
                }
            });
        }
    }, [rightPanelActiveTab, selectedFeature]);

    useEffect(() => {
        computeTrueNorth(selectedFeature?.attributes?.lighthouseData?.center).then((angle) => {
            dispatch(setCurrentLighthouseTrueNorth(angle));
        });

        return () => {
            if (selectedFeatureHighlightRef?.current) {
                selectedFeatureHighlightRef.current.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (!AppConfig.Lighthouse.AdaptiveSectorThickness || !selectedFeature?.attributes.lighthouseData) return;

        const mapview = getMapView();
        if (!mapview?.map) return;

        const lighthouse = lighthouseContext?.lighthouseData.value;
        if (!lighthouse) return;

        const lighthouseEditingLayer = mapview.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
        const lighthouseGraphic = lighthouseEditingLayer.graphics.find(
            (graphic) => graphic.attributes?.id === lighthouse.id
        );

        if (!lighthouseGraphic) return;

        const showAllSectors = showSectorColors
            ? new Array(lighthouseGraphic.attributes.lighthouseData.sectors.length).fill(0).map((_, i) => i)
            : [];

        drawSectors({
            graphicsLayer: lighthouseEditingLayer,
            data: lighthouse,
            setIsEditing: (value) => dispatch(setIsDrawing(value)),
            sectorsToFill: lighthouseTableSelectedSector === null ? showAllSectors : [lighthouseTableSelectedSector],
            opacity: sectorOpacity,
            drawNumbers: false,
            trueNorthCorrection: trueNorth,
            centerFeature: selectedFeature,
        });
    }, [mapScale]);

    useEffect(() => {
        if (activeVersion) {
            const user = userContext?.user.value;
            canEditVersion(activeVersion, versionsSharedWithMe ?? [], user).then((result) => {
                setCanIEditVersion(result);
            });
        }
    }, [activeVersion, versionsSharedWithMe]);

    const discontinueOrRemove = async (newStatus: NFSStatus) => {
        if (!selectedFeature) return;
        if (hasUnsavedChanges) {
            toast.warning("Fullfør flytting før du endrer status.");
            return;
        }

        dispatch(setLoading(true));
        selectedFeature.attributes.nfs_status = newStatus;
        setOptionsPopoverOpen(false);

        if (isLighthouse(selectedFeature)) {
            const view = getMapView();
            if (!view) return;
            const lightLayer = view.map?.findLayerById("Lys") as FeatureLayer;
            await loadLighthouse(
                selectedFeature.attributes.objectid,
                lightLayer,
                setLighthouseData,
                appContext?.selectedFeature.set,
                (loadingStatus: boolean) => dispatch(setLoading(loadingStatus)),
                () => {},
                selectedFeature,
                false
            );

            const lighthouseData = lighthouseContext!.lighthouseDataRef.value;
            for (const sector of lighthouseData?.current.sectors ?? []) {
                sector.nfs_status = newStatus;
                sector.nfs_status_line1 = newStatus;
                sector.nfs_status_line2 = newStatus;
            }
            lighthouseContext?.lighthouseData.set({ ...lighthouseData?.current });
            const trueNorth = await computeTrueNorth(lighthouseData.current.center);

            await saveLighthouse(
                lighthouseData.current,
                appContext?.selectedFeature.set,
                selectedFeature,
                trueNorth,
                (isSaving) => dispatch(setIsSavingLighthouse(isSaving)),
                (loadingStatus) => dispatch(setLoading(loadingStatus))
            );
        }
        dispatch(setLoading(true));

        const jobGlobalId = await discontinueOrRemoveNauticalObject(
            activeVersion?.versionIdentifier.name ?? "",
            activeVersion?.versionIdentifier.guid ?? "",
            selectedFeature,
            setSelectedFeature,
            setShownFeature,
            (value) => dispatch(setLoading(value))
        );

        const { features: jobs } = await getJobs(JobsStatus.Plan);
        const newJob = jobs.find((job) => job.attributes.globalid === jobGlobalId);

        dispatch(setLoading(false));
        if (!newJob) return;

        dispatch(setLeftPanelActiveTab(LeftPanelTabs.UNDEFINED));

        jobContext?.selectedJob.set(newJob);
        dispatch(setRightPanelActiveTab(RightPanelTabs.JOBS));
    };

    const openJob = async (job: Graphic | null) => {
        if (!job) {
            toast.error("Kunne ikke finne jobben. Du kan prøve å oppdatere listen over jobber i jobblisten.");
        }

        jobContext?.selectedJob.set(job);
        dispatch(setRightPanelActiveTab(RightPanelTabs.JOBS));
    };

    const setNewFeature = async (feature: Graphic, options?: ReplaceOptions) => {
        // A lighthouse is created and saved before it gets here, so it names the object it
        // replaces explicitly - creating it swaps the selected feature out from under us.
        const replacedFeature = options?.replacedFeature ?? selectedFeature;
        if (!replacedFeature) return;

        if (hasUnsavedChanges) {
            toast.warning("Fullfør flytting før du erstatter objektet.");
            return;
        }

        dispatch(setLoading(true));

        const oldObject = replacedFeature.clone();
        oldObject.layer = replacedFeature.layer;

        feature.attributes.navn = oldObject.attributes.navn;
        feature.attributes.sted = oldObject.attributes.sted;
        feature.attributes.beliggenhet = oldObject.attributes.beliggenhet;
        feature.attributes.kategori = oldObject.attributes.kategori;
        feature.attributes.navigasjonsinstallasjon_eier = oldObject.attributes.navigasjonsinstallasjon_eier;
        feature.geometry = oldObject.geometry;

        // If both the old and the new objects have lys_hoyde and byggehoyde attributes these should also be copied
        if ("lys_hoyde" in oldObject.attributes && "lys_hoyde" in feature.attributes) {
            feature.attributes.lys_hoyde = oldObject.attributes.lys_hoyde;
        }
        if ("byggehoyde" in oldObject.attributes && "byggehoyde" in feature.attributes) {
            feature.attributes.byggehoyde = oldObject.attributes.byggehoyde;
        }
        if ("sjomerke_nr" in oldObject.attributes && "sjomerke_nr" in feature.attributes) {
            feature.attributes.sjomerke_nr = oldObject.attributes.sjomerke_nr;
        }

        // The new object stands where the old one stood, so region and rode carry over. A
        // lighthouse is created before it gets here and skips createRelatedFeatures' own
        // lookup below, so this is the only place both replace paths pass through.
        if ("kystverk_region" in feature.attributes || "kystverk_rode" in feature.attributes) {
            const { region, rode } = await resolveKystverketRegionAndRode(feature, oldObject);
            if (region !== null && "kystverk_region" in feature.attributes) {
                feature.attributes.kystverk_region = region;
            }
            if (rode !== null && "kystverk_rode" in feature.attributes) {
                feature.attributes.kystverk_rode = rode;
            }
        }

        // Replacing a fyrlykt or fyrstasjon with another one keeps the light calculation - the
        // new object stands in for the old one, so recalculating it from presets is busywork.
        if (isLighthouse(oldObject) && isLighthouse(feature)) {
            copyLightCalculationValues(oldObject, feature);
        }

        const createObjectCurrentEntry = appContext?.createObjectCurrentEntryRef.value.current;
        const selectedLayerConfig = AppConfig.CreateObject.flatMap((group) => group.GroupEntries).find(
            (entry) => entry.Title === createObjectCurrentEntry
        );

        // The replacement itself updates the new feature, so it has to exist first. Everything
        // but a lighthouse is added here; a lighthouse was already added when it was saved.
        await createRelatedFeatures(
            feature,
            feature.layer as FeatureLayer,
            selectedLayerConfig,
            userName,
            !options?.isAlreadyCreated
        );

        setOptionsPopoverOpen(false);
        const jobGlobalId = await replaceNauticalObject(
            oldObject,
            feature,
            activeVersion?.versionIdentifier.name ?? "",
            activeVersion?.versionIdentifier.guid ?? ""
        );

        if (!jobGlobalId) {
            toast.error("Noe gikk galt ved erstatning av objektet.");
            dispatch(setLoading(false));
            return;
        }

        const job = await getJobByGlobalId(jobGlobalId);

        toast.info(
            <div>
                <p>Du har erstattet ett objekt, når du er ferdig med å redigere det nye kan du klargjøre jobben.</p>
                <button className="nfs-button" onClick={() => openJob(job)}>
                    Gå til jobben
                </button>
            </div>,
            { autoClose: false, icon: <InfoIcon /> }
        );

        setShownFeature?.(oldObject);
        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));

        setSelectedFeature?.(feature);
        dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));
        setIsReplacing(false);
        dispatch(setLoading(false));
    };

    const exportCableAsShape = () => {
        if (!selectedFeature) return;
        const email = userContext?.user.value?.email ?? "";
        const fileName =
            "sjøkabel_" +
            (selectedFeature.attributes.navn ?? selectedFeature.attributes.objectid) +
            "_" +
            Date.now().toLocaleString() +
            ".shp";

        exportFeatureToShape(selectedFeature, fileName, email);
    };

    const featureExists = () => {
        if (!selectedFeature) return false;
        const status = selectedFeature?.attributes["nfs_status"] ?? NFSStatus.UNDEFINED;
        return status !== NFSStatus.DISCONTINUED && status !== NFSStatus.REMOVED;
    };

    const sendFeatureToPlanroot = async () => {
        setIsSendingHelperPoint(true);
        const versionGuid = activeGuid;
        if (!versionGuid || !selectedFeature) {
            setIsSendingHelperPoint(false);
            toast.error("Kunne ikke sende hjelpepunktendring til planroot.");
            return;
        }
        await postHelperPointChangeToPlanRoot(selectedFeature, versionGuid);
        setIsSendingHelperPoint(false);
    };

    const deleteHelperPointAndUpdatePlanroot = async () => {
        if (hasUnsavedChanges) {
            toast.warning("Fullfør flytting før du sletter hjelpepunkt.");
            return;
        }
        setIsDeletingHelperPoint(true);
        const versionGuid = activeGuid;
        if (!versionGuid || !selectedFeature) {
            setIsDeletingHelperPoint(false);
            toast.error("Kunne ikke slette hjelpepunkt.");
            return;
        }
        await deleteHelperpoint(selectedFeature, versionGuid);
        appContext.selectedFeature.set(null);
        appContext.clickResults.set([]);
        appContext.shownFeature.set(null);
        appContext.searchResults.set([]);
        setIsDeletingHelperPoint(false);
        dispatch(setLeftPanelActiveTab(LeftPanelTabs.UNDEFINED));
        dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
    };

    const tryDelete = async () => {
        setOptionsPopoverOpen(false);
        if (!selectedFeature || !activeVersion?.versionIdentifier.name) {
            toast.error("Kunne ikke slette objektet.");
            return;
        }
        if (hasUnsavedChanges) {
            toast.warning("Fullfør flytting før du sletter objektet.");
            return;
        }
        dispatch(setLoading(true));
        try {
            const result = await deleteIfOnlyInVersion(
                selectedFeature,
                activeGuid,
                activeVersion?.versionIdentifier.name,
                true
            );
            if (result) {
                const sectorlineLayer = getMapView()?.map?.findLayerById("Sektorlinje");
                if (sectorlineLayer) {
                    (sectorlineLayer as FeatureLayer).refresh();
                }
                const sectorAreaLayer = getMapView()?.map?.findLayerById("Sektorflate");
                if (sectorAreaLayer) {
                    (sectorAreaLayer as FeatureLayer).refresh();
                }
                toast.success("Objektet ble slettet.");
                setSelectedFeature?.(null);
                dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
            } else {
                toast.error("Noe gikk galt ved sletting av objektet.");
            }
            dispatch(setLoading(false));
        } catch (error) {
            toast.error("Noe gikk galt ved sletting av objektet.");
            dispatch(setLoading(false));
        }
    };

    const allowStatusChange = (): boolean => {
        if (!selectedFeature) return false;
        const featureLayerId = (selectedFeature.layer as FeatureLayer)?.layerId;
        if (featureLayerId === undefined || featureLayerId === null) return false;
        if ([FeatureClasses.Hjelpepunkt].includes(featureLayerId)) return false;
        switch (selectedFeature.attributes.nfs_status) {
            case NFSStatus.PROJECTED:
            case NFSStatus.REMOVED:
            case NFSStatus.DISCONTINUED:
            case NFSStatus.PROJECTED_EXISTING:
            case NFSStatus.PROJECTED_DISCONTINUED:
            case NFSStatus.PROJECTED_REMOVED:
                return false;
            default:
                return true;
        }
    };

    const canUseRestoreMenu = () => {
        if (!selectedFeature) return false;
        const status = selectedFeature?.attributes["nfs_status"];
        if (
            status === NFSStatus.DISCONTINUED ||
            status === NFSStatus.REMOVED ||
            status === NFSStatus.OPERATIVE ||
            status === NFSStatus.PROJECTED
        ) {
            return false;
        }
        if (!restorableObjects.defaultFeature && !restorableObjects.operativeFeature) return false;
        const featureLayerId = (selectedFeature.layer as FeatureLayer)?.layerId;
        if (featureLayerId === undefined || featureLayerId === null) return false;
        if ([FeatureClasses.Hjelpepunkt].includes(featureLayerId)) return false;
        return true;
    };

    const canUseDelete = () => {
        if (!selectedFeature) return false;
        const featureLayerId = (selectedFeature.layer as FeatureLayer)?.layerId;
        if (featureLayerId === undefined || featureLayerId === null) return false;
        if ([FeatureClasses.Hjelpepunkt].includes(featureLayerId)) return false;
        return true;
    };

    // The tilknyttet_* flags aren't reliably set on existing data, so also check the pair resolved
    // through the relationship to Lys
    const isSelectedPairedSecondary = () =>
        !!fastPathPair?.secondary &&
        fastPathPair.secondary.attributes?.globalid === selectedFeature?.attributes?.globalid;

    const shouldShow3DotMenu = () => {
        // List of layer IDs where the 3-dot menu should be shown subject to further checks
        const validLayerid = [0, 1, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15];
        if (!selectedFeature) return false;
        const featureLayerId = (selectedFeature.layer as FeatureLayer)?.layerId;
        if (featureLayerId === undefined || featureLayerId === null) return false;
        if (!validLayerid.includes(featureLayerId)) return false;
        // Actions on a non-primary object (child layer or paired secondary) can split the combined object
        if (isNonPrimaryFeature(selectedFeature) || isSelectedPairedSecondary()) return false;
        return true;
    };

    const editFeatureAttribute = (field: string, value: any) => {
        if (!selectedFeature) return;
        selectedFeature.attributes[field] = value;
        const clonedFeature = selectedFeature.clone();
        clonedFeature.layer = selectedFeature.layer;
        setNewStatusToFeature(clonedFeature, NFSStatus.PROJECTED_EXISTING);
        setSelectedFeature?.(clonedFeature);
    };

    const canSendToFDV = () => {
        if (!selectedFeature) return false;
        const featureName = selectedFeature.attributes.navn;
        const featureSeamarkNumber = selectedFeature.attributes.sjomerke_nr;
        const featureLocation = selectedFeature.attributes.sted;

        return !(featureName && featureSeamarkNumber && featureLocation);
    };

    const featureHasMissingGisObjId = () => {
        if (!selectedFeature) return false;
        const hasAttribute = selectedFeature.attributes.hasOwnProperty("gis_obj_id");
        if (!hasAttribute) return false;
        return !selectedFeature.attributes["gis_obj_id"];
    };

    const sendObjectToFDV = async () => {
        if (!selectedFeature) {
            return;
        }

        if (selectedFeature.attributes["gis_obj_id"]) {
            console.warn("the feature already has a gis_obj_id");
            return;
        }

        if (selectedFeature.attributes["nfs_status"] !== NFSStatus.PROJECTED) {
            console.warn("the feature status is not Prosjektert, cannot update FDV object");
            return;
        }

        try {
            setIsFDVSending(true);
            const versionName = (selectedFeature.layer as FeatureLayer).gdbVersion as string;
            const fdvResult = await updateFDVObjectNew(
                {
                    layerId: (selectedFeature.layer as FeatureLayer).layerId,
                    globalId: selectedFeature.attributes.globalid,
                    gdbVersion: versionName,
                },
                selectedFeature
            );

            const newGisObjId = fdvResult.feature?.attributes["gis_obj_id"];
            console.log("Feature created in FDV with gis_obj_id:", newGisObjId);
            editFeatureAttribute("gis_obj_id", newGisObjId);
        } catch (error) {
            console.error("Error sending object to FDV:", error);
        } finally {
            setIsFDVSending(false);
        }
    };

    const canCreateJob = () => {
        if (!selectedFeature) return false;

        if (
            hasEditedAttributes ||
            [
                NFSStatus.PROJECTED,
                NFSStatus.PROJECTED_EXISTING,
                NFSStatus.PROJECTED_REMOVED,
                NFSStatus.PROJECTED_DISCONTINUED,
            ].includes(selectedFeature.attributes["nfs_status"])
        ) {
            return true;
        }

        return false;
    };

    const createJobForObject = async () => {
        if (!selectedFeature) return;
        if (!canIEditVersion) return;

        // Jobs are created on the main feature. If the editor is open on a child
        // feature, resolve up to its main feature first (usually a no-op, since
        // map-click already resolves via getMainRelatedFeature). Resolution is
        // best-effort: if it throws, fall back to the selected feature.
        let resolvedFeature = selectedFeature;
        try {
            resolvedFeature = (await getMainRelatedFeature(selectedFeature)) ?? selectedFeature;
        } catch (error) {
            console.error("Failed to resolve main feature for job creation:", error);
        }

        // Offer the Plan/QA1 jobs the object is already in before creating a new one. The
        // check is best-effort: if it fails, fall back to creating a new job as before.
        setIsCheckingExistingJobs(true);
        let jobs: Graphic[] = [];
        try {
            jobs = await getOpenJobsForObject(
                [resolvedFeature.attributes.globalid, selectedFeature.attributes.globalid],
                activeGuid
            );
        } catch (error) {
            console.error("Failed to look up existing jobs for object:", error);
        } finally {
            setIsCheckingExistingJobs(false);
        }

        if (jobs.length > 0) {
            existingJobsFeatureRef.current = resolvedFeature;
            setExistingJobs(jobs);
            return;
        }

        startNewJobForObject(resolvedFeature);
    };

    // A list of jobs found for one object must not stay open for the next one.
    useEffect(() => {
        setExistingJobs([]);
        existingJobsFeatureRef.current = null;
    }, [selectedFeature?.attributes.globalid]);

    const startNewJobForObject = (resolvedFeature: Graphic) => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.JOBS));
        if (!result) return;

        const layer = resolvedFeature.layer as FeatureLayer;
        const layerID = layer.layerId;
        if (layerID === null || layerID === undefined) return;
        const objectIdField = layer.objectIdField;
        const objectId = resolvedFeature.attributes[objectIdField];

        jobContext?.preLoadJobConfig.set({ layerID: layerID, objectId: objectId });
    };

    const postFastPathToPlanroot = async (promotionConfirmed: boolean) => {
        if (!selectedFeature) return;
        if (!activeGuid) {
            toast.error("Mangler aktiv versjon. Kan ikke poste til planroot.");
            return;
        }
        const versionName = activeVersion?.versionIdentifier.name;
        if (!versionName) {
            toast.error("Mangler versjonsnavn. Kan ikke poste til planroot.");
            return;
        }

        setIsFastPathBusy(true);
        try {
            const result = await runFastPath(selectedFeature, activeGuid, versionName, { promotionConfirmed });
            if (result.ok) {
                // Refresh the cached DEFAULT snapshot so the diff-vs-default effect recomputes
                // to "no changes" naturally (otherwise the button could re-appear on the next
                // render until something else triggers restorableObjects to refresh).
                const refreshed = await getDefaultAndOperativeFeature(selectedFeature);
                setRestorableObjects(refreshed);

                // Refresh both sides' DEFAULT snapshots so the gate clears. `pair.primary`
                // may or may not be the selectedFeature (it's selected when the user is on
                // Lys; it's the partner when the user is on the secondary side).
                if (fastPathPair) {
                    const refreshedPrimary = await getDefaultAndOperativeFeature(fastPathPair.primary, true);
                    setFastPathPrimaryDefault(refreshedPrimary.defaultFeature);
                    if (fastPathPair.secondary) {
                        const refreshedSecondary = await getDefaultAndOperativeFeature(fastPathPair.secondary, true);
                        setFastPathSecondaryDefault(refreshedSecondary.defaultFeature);
                    }
                }

                // runFastPath mutated selectedFeature.attributes[statusField] in place (to
                // OPERATIVE) and persisted it, but no React state holds that mutation — so
                // anything reading from appContext.selectedFeature still sees the old
                // reference and won't re-render. Push a fresh clone so the UI reflects the
                // restored status and the "changed" indicator clears.
                const refreshedSelected = selectedFeature.clone();
                refreshedSelected.layer = selectedFeature.layer;
                appContext?.selectedFeature.set(refreshedSelected);
            }
            // All user-facing messaging (success, validation errors, post failures, SKSD
            // outcomes) is handled by the progress toast inside runFastPath.
        } finally {
            setIsFastPathBusy(false);
        }
    };

    const canCreateDroneControlJob = () => {
        if (!selectedFeature) return false;
        const layerID = (selectedFeature.layer as FeatureLayer).layerId;
        const classId = selectedFeature.attributes?.klasse;

        if (
            [NauticalClasses.FL_Fyrlykt, NauticalClasses.FS_Fyrstasjon].includes(classId) &&
            layerID === FeatureClasses.Lys &&
            selectedFeature.attributes["nfs_status"] === NFSStatus.OPERATIVE
        ) {
            return true;
        }
        return false;
    };

    const createDroneControlJob = async () => {
        try {
            setShowLoader(true);
            if (!selectedFeature) {
                return;
            }

            const nauticalClass = GetCodedValueAndAlias(
                selectedFeature.layer,
                "klasse",
                selectedFeature.attributes["klasse"]
            )[1] as string;
            const sjomerkeNummer = selectedFeature.attributes.sjomerke_nr;
            const jobName = `Droneskjerming ${nauticalClass.substring(0, 2)} ${sjomerkeNummer} ${selectedFeature.attributes.navn}`;
            const jobDescription = `Kontrollskjerming med drone`;

            const job = await createNewJob(jobName, jobDescription, JobsType.DRONE_CONTROL, sjomerkeNummer);

            if (!job?.globalId) {
                console.error("Failed to create job");
                toast.error("Noe gikk galt ved opprettelse av jobb.");
                return;
            }

            const objectInJob = await addObjectToJobSimple(
                job.globalId,
                selectedFeature.attributes.globalid,
                (selectedFeature.layer as FeatureLayer).layerId!
            );

            if (!objectInJob) {
                console.error("Failed to add object to job");
                toast.error("Noe gikk galt ved kobling av objektet til jobben.");
                return;
            }

            const nextStepGuid = await createNewJobStep(job.globalId, JobsStatus.TilUtføring, "");

            if (!nextStepGuid) {
                console.error("Failed to create job step");
                toast.error("Noe gikk galt ved opprettelse av jobbsteg.");
                return;
            }

            dispatch(changeRightPanelTab(RightPanelTabs.UNDEFINED));
            toast.success(
                <div>
                    <div>Droneskjermingsjobb opprettet.</div>
                    <button
                        style={{
                            marginTop: "4px",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "inherit",
                            textDecoration: "underline",
                            fontSize: "inherit",
                        }}
                        onClick={() => navigator.clipboard.writeText(job.globalId!)}
                    >
                        Kopier jobb-ID til utklippstavle
                    </button>
                </div>,
                { autoClose: false }
            );
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        if (!selectedFeature) return;
        if (!canIEditVersion) return;

        setIsLoadingRestorableObjects(true);
        getDefaultAndOperativeFeature(selectedFeature).then((result) => {
            setRestorableObjects(result);
            setIsLoadingRestorableObjects(false);
        });
        checkIfInsert(selectedFeature, activeGuid).then((result) => {
            setAllowDelete(result);
        });
    }, [selectedFeature?.attributes.globalid, canIEditVersion]);

    // Resolve the (primary, secondary) pair whenever the selection changes — bidirectional:
    // - selected = Lys (LB/HIB/FL klasse) → primary = selected, secondary = related partner
    // - selected = IB/Flytendemerke with a Lys partner → secondary = selected, primary = Lys
    // - anything else → primary = selected, secondary = null
    // We also fetch both DEFAULT snapshots here so the diff effect downstream has what it
    // needs in one pass. runFastPath re-resolves independently; these slots are display-only.
    useEffect(() => {
        let cancelled = false;
        if (!selectedFeature || !canIEditVersion) {
            setFastPathPair(null);
            setFastPathPrimaryDefault(null);
            setFastPathSecondaryDefault(null);
            setFastPathRelatedSectorChanged(false);
            return;
        }
        resolveFastPathPair(selectedFeature)
            .then(async (pair) => {
                if (cancelled) return;
                setFastPathPair(pair);
                const [primaryDefault, secondaryDefault] = await Promise.all([
                    getDefaultAndOperativeFeature(pair.primary, true).then((r) => r.defaultFeature),
                    pair.secondary
                        ? getDefaultAndOperativeFeature(pair.secondary, true).then((r) => r.defaultFeature)
                        : Promise.resolve(null),
                ]);
                if (cancelled) return;
                setFastPathPrimaryDefault(primaryDefault);
                setFastPathSecondaryDefault(secondaryDefault);
            })
            .catch((error: unknown) => {
                console.error("[fast-path] failed to resolve pair:", error);
                if (!cancelled) {
                    setFastPathPair(null);
                    setFastPathPrimaryDefault(null);
                    setFastPathSecondaryDefault(null);
                    setFastPathRelatedSectorChanged(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [selectedFeature?.attributes.globalid, selectedFeature?.attributes.klasse, canIEditVersion]);

    // Rebind the side of `fastPathPair` that matches the selected feature whenever
    // `appContext.selectedFeature` swaps to a fresh clone (every field save). Without this,
    // pair.primary / pair.secondary stay on the pre-edit Graphic reference: the field value
    // was mutated in place by FeatureField, but `nfs_status` was only bumped to
    // PROJECTED_EXISTING on the clone. That leaves the diff effect inert (stable deps) and
    // the JSX gate reading a stale OPERATIVE status. The resolve effect above stays keyed on
    // globalid/klasse so we don't re-fire the related-features network query on every keystroke.
    useEffect(() => {
        if (!fastPathPair || !selectedFeature) return;
        const sameGlobalid = (g: Graphic | null | undefined) =>
            g?.attributes?.globalid === selectedFeature.attributes?.globalid;
        if (sameGlobalid(fastPathPair.primary) && fastPathPair.primary !== selectedFeature) {
            setFastPathPair({ ...fastPathPair, primary: selectedFeature });
        } else if (sameGlobalid(fastPathPair.secondary) && fastPathPair.secondary !== selectedFeature) {
            setFastPathPair({ ...fastPathPair, secondary: selectedFeature });
        }
    }, [selectedFeature, fastPathPair]);

    // Diff the primary (Lys-side) against its DEFAULT. Strict diff — no extra ignored fields,
    // since `SECONDARY_ONLY_IGNORED_FIELDS` covers attributes that only matter on the partner.
    useEffect(() => {
        if (!fastPathPair || !fastPathPrimaryDefault) {
            setFastPathPrimaryChanges([]);
            setFastPathPrimaryGeometryChanged(false);
            return;
        }
        const changes = computeChangedFieldsVsDefault(fastPathPair.primary, fastPathPrimaryDefault);
        setFastPathPrimaryChanges(changes);

        // Use the ArcGIS geometry equals operator so server-side precision/serialization
        // drift doesn't flag the button as blocked when the user never actually moved the
        // feature.
        const versionGeom = fastPathPair.primary.geometry ?? null;
        const defaultGeom = fastPathPrimaryDefault.geometry ?? null;
        let geometryChanged: boolean;
        if (versionGeom === null && defaultGeom === null) {
            geometryChanged = false;
        } else if (versionGeom === null || defaultGeom === null) {
            geometryChanged = true;
        } else {
            geometryChanged = !equalsOperator.execute(versionGeom, defaultGeom);
        }
        setFastPathPrimaryGeometryChanged(geometryChanged);
    }, [fastPathPair, fastPathPrimaryDefault]);

    // A light with ANY changed related sector (Lyssektor 19 / Sektorlinje 2 / Sektorflate 3)
    // must go through the normal job flow, not the fast path, so the sector edits aren't
    // stranded in DEFAULT. Mirror the orchestrator's phase-A check here so the button is
    // disabled with a reason. detectRelatedSectorChanges already fails closed internally.
    useEffect(() => {
        let cancelled = false;
        if (!fastPathPair) {
            setFastPathRelatedSectorChanged(false);
            return;
        }
        detectRelatedSectorChanges(activeGuid, fastPathPair.primary, fastPathPrimaryDefault)
            .then((changed) => {
                if (!cancelled) setFastPathRelatedSectorChanged(changed);
            })
            .catch(() => {
                if (!cancelled) setFastPathRelatedSectorChanged(true); // fail closed
            });
        return () => {
            cancelled = true;
        };
        // Keyed on the primary's globalid (not the `fastPathPair` object) so attribute edits —
        // which the rebind effect above clones into a fresh pair on every save — don't re-fire
        // this network query. Safe because detectRelatedSectorChanges uses only the Graphic's
        // identity (layer + objectid) to walk relationships, never its live attribute values.
    }, [fastPathPair?.primary?.attributes?.globalid, fastPathPrimaryDefault, activeGuid]);

    useEffect(() => {
        if (!fastPathPair?.secondary || !fastPathSecondaryDefault) {
            setFastPathSecondaryChanges([]);
            setFastPathSecondaryGeometryChanged(false);
            return;
        }
        const changes = computeChangedFieldsVsDefault(
            fastPathPair.secondary,
            fastPathSecondaryDefault,
            SECONDARY_ONLY_IGNORED_FIELDS
        );
        setFastPathSecondaryChanges(changes);

        const versionGeom = fastPathPair.secondary.geometry ?? null;
        const defaultGeom = fastPathSecondaryDefault.geometry ?? null;
        let geometryChanged: boolean;
        if (versionGeom === null && defaultGeom === null) {
            geometryChanged = false;
        } else if (versionGeom === null || defaultGeom === null) {
            geometryChanged = true;
        } else {
            geometryChanged = !equalsOperator.execute(versionGeom, defaultGeom);
        }
        setFastPathSecondaryGeometryChanged(geometryChanged);
    }, [fastPathPair, fastPathSecondaryDefault]);

    if (!selectedFeature || !canIEditVersion) return;

    return (
        <div className={`feature-viewer`}>
            {showLoader && <Loader color="#000667" overlay="element" targetElement=".feature-viewer" size={40} />}
            {isFastPathBusy && <Loader color="#000667" overlay="fullscreen" size={60} />}
            <div className="feature-viewer-header" style={{ paddingBottom: 0 }}>
                <span className="flex-row small-gap w-100"></span>
                {shouldShow3DotMenu() && (
                    <Popover
                        isOpen={optionsPopoverOpen}
                        onClickOutside={() => setOptionsPopoverOpen(false)}
                        content={
                            <div className="options-popover-content">
                                <span className="visible-when-alone">Ingen gyldige handlinger</span>
                                {canBeSentToFdv && featureHasMissingGisObjId() && (
                                    /*!codedValue && props.isEditing && */ <NfsButton
                                        title="Krever Navn, Sted og Sjømerkenummer for å sende"
                                        onClick={sendObjectToFDV}
                                        disabled={canSendToFDV()}
                                    >
                                        {isFDVSending ? (
                                            <>
                                                Sender... <Loader color="#000667" overlay={false} size={20} />
                                            </>
                                        ) : (
                                            "Opprette objekt i FDV"
                                        )}
                                    </NfsButton>
                                )}
                                {allowStatusChange() && (
                                    <>
                                        <NfsButton
                                            onClick={async () => {
                                                await discontinueOrRemove(NFSStatus.PROJECTED_DISCONTINUED);
                                            }}
                                        >
                                            Nedlegg
                                        </NfsButton>
                                        <NfsButton
                                            onClick={async () => {
                                                await discontinueOrRemove(NFSStatus.PROJECTED_REMOVED);
                                            }}
                                        >
                                            Fjern
                                        </NfsButton>
                                        <NfsButton
                                            onClick={() => {
                                                setIsReplacing(!isReplacing);
                                            }}
                                            outlined={isReplacing}
                                        >
                                            Erstatt
                                        </NfsButton>
                                    </>
                                )}
                                {isReplacing && (
                                    <div className="all-objects-replace">
                                        <AllObjectTypesPanel setNewFeature={setNewFeature} isReplacing />
                                    </div>
                                )}
                                {featureExists() &&
                                    !isDefaultVersion(
                                        versionInfo ?? null,
                                        versionContext?.versionServiceRef.value.current!
                                    ) &&
                                    canHaveSectors(selectedFeature) && (
                                        <NfsButton onClick={() => setIsScreenReportModalOpen(true)}>
                                            Skjermingsrapport
                                        </NfsButton>
                                    )}
                                {featureExists() &&
                                    canUseRestoreMenu() &&
                                    !isDefaultVersion(
                                        versionInfo ?? null,
                                        versionContext?.versionServiceRef.value.current!
                                    ) && (
                                        <NfsButton onClick={() => setIsRestoreModalOpen(true)}>
                                            Gjenopprettingsmeny
                                        </NfsButton>
                                    )}
                                {featureExists() &&
                                    canUseDelete() &&
                                    !isDefaultVersion(
                                        versionInfo ?? null,
                                        versionContext?.versionServiceRef.value.current!
                                    ) && (
                                        <ConfirmationPopover
                                            disabled={!allowDelete}
                                            onConfirm={tryDelete}
                                            confirmText="Slett"
                                            title="Bekreft sletting"
                                            message="Er du sikker på at du vil slette dette objektet? Dette kan ikke angres."
                                            warning
                                            onCancel={() => {}}
                                        >
                                            <div
                                                className="nfs-button"
                                                style={{ background: "red", color: "white", border: "none" }}
                                            >
                                                Slett objekt
                                            </div>
                                        </ConfirmationPopover>
                                    )}
                            </div>
                        }
                    >
                        <button
                            type="button"
                            className="empty-button"
                            onClick={() => {
                                setOptionsPopoverOpen(!optionsPopoverOpen);
                            }}
                        >
                            <OptionsIcon />
                        </button>
                    </Popover>
                )}

                <button type="button" className="empty-button" onClick={unselectFeature}>
                    <CloseIcon />
                </button>
            </div>

            <FeatureViewerContent
                canBeSentToFdv={canBeSentToFdv}
                setCanBeSentToFdv={setCanBeSentToFdv}
                isEditing={featureExists()}
                feature={selectedFeature}
                canMove={canMove && !isSelectedPairedSecondary()}
                setHasEditedAttributes={setHasEditedAttributes}
            />
            <div className="feature-viewer-tools">
                {featureExists() && featureHasLight(selectedFeature) && layerAllowsLightCalc(selectedFeature) && (
                    <button className="feature-viewer-tool" onClick={openLightCalculation}>
                        <LightBulbIcon />
                        <span>Beregn lys</span>
                    </button>
                )}
                {featureExists() &&
                    !isDefaultVersion(versionInfo ?? null, versionContext?.versionServiceRef.value.current!) &&
                    canHaveSectors(selectedFeature) && (
                        <button className="feature-viewer-tool" onClick={openSectorEditing}>
                            <SectorLinesIcon />
                            <span>Redigér sektorer</span>
                        </button>
                    )}
                {featureExists() && isPartOfOverett && <OverettButton />}
                {selectedFeature.layer?.id === "Sjøkabel" && (
                    <button className="feature-viewer-tool flex-row" onClick={exportCableAsShape}>
                        <DownloadIcon />
                        <span>Eksporter sjøkabel</span>
                    </button>
                )}
                {featureExists() && selectedFeature.layer?.id === "Sjøkabel" && (
                    <button className="feature-viewer-tool" onClick={openSeaCableEditing}>
                        <span>Rediger sjøkabel</span>
                    </button>
                )}
                {selectedFeature.layer?.id === "Hjelpepunkt" && (
                    <>
                        <button
                            className="feature-viewer-tool"
                            onClick={sendFeatureToPlanroot}
                            disabled={isSendingHelperPoint || isDeletingHelperPoint}
                        >
                            <span className="flex-row small-gap">
                                {isSendingHelperPoint && <Loader size={16} inline />}
                                {isSendingHelperPoint ? (
                                    <span>Sender til Planroot</span>
                                ) : (
                                    <span>Send til Planroot</span>
                                )}
                            </span>
                        </button>
                        <button
                            className="feature-viewer-tool"
                            onClick={deleteHelperPointAndUpdatePlanroot}
                            disabled={isSendingHelperPoint || isDeletingHelperPoint}
                        >
                            <span className="flex-row small-gap">
                                {isDeletingHelperPoint && <Loader size={16} inline />}
                                {isDeletingHelperPoint ? (
                                    <span>Sletter hjelpepunkt</span>
                                ) : (
                                    <span>Slett hjelpepunkt</span>
                                )}
                            </span>
                        </button>
                    </>
                )}
            </div>
            <div className="feature-viewer-footer">
                <NfsButton
                    onClick={() => {
                        setShownFeature?.(selectedFeature);
                        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));
                    }}
                >
                    Vis
                </NfsButton>
                {canCreateJob() && (
                    <ExistingJobsPopover
                        isOpen={existingJobs.length > 0}
                        jobs={existingJobs}
                        onOpenJob={(job) => {
                            setExistingJobs([]);
                            openJob(job);
                        }}
                        onCreateNew={() => {
                            setExistingJobs([]);
                            if (existingJobsFeatureRef.current) startNewJobForObject(existingJobsFeatureRef.current);
                        }}
                        onClose={() => setExistingJobs([])}
                    >
                        <NfsButton onClick={createJobForObject} disabled={isCheckingExistingJobs}>
                            {isCheckingExistingJobs ? (
                                <>
                                    Sjekker jobber… <Loader color="#000667" overlay={false} size={20} />
                                </>
                            ) : (
                                "Opprett jobb på objekt"
                            )}
                        </NfsButton>
                    </ExistingJobsPopover>
                )}
                {AppConfig.FeatureToggles?.DroneControlJobs && !canCreateJob() && canCreateDroneControlJob() && (
                    <ConfirmationPopover
                        onConfirm={createDroneControlJob}
                        confirmText="Opprett"
                        title="Bekreft droneskjermingsjobb"
                        message="Er du sikker på at du vil opprette en droneskjermingsjobb for dette objektet?"
                        onCancel={() => {}}
                        buttonClass="feature-viewer-footer-button"
                    >
                        <div className="nfs-button">Droneskjermingsjobb</div>
                    </ConfirmationPopover>
                )}
                {(() => {
                    // Pair-aware gate driven by the shared evaluator (single source of truth
                    // with the orchestrator). Renders the post button when canPost; otherwise,
                    // when the object has pending changes, renders a small info icon explaining
                    // why direct posting is unavailable.
                    if (!fastPathPair) return null;
                    const { primary, secondary } = fastPathPair;

                    const primaryLayerId = (primary.layer as FeatureLayer | undefined)?.layerId;
                    const primaryStatusField = getStatusFieldName(primary);
                    const primaryStatus = primaryStatusField ? primary.attributes?.[primaryStatusField] : undefined;
                    if (primaryLayerId === undefined || primaryLayerId === null || primaryStatus === undefined) {
                        return null;
                    }
                    const primaryDefaultStatus =
                        primaryStatusField && fastPathPrimaryDefault
                            ? (fastPathPrimaryDefault.attributes?.[primaryStatusField] as number | undefined)
                            : undefined;

                    let secondaryInput: FastPathSideInput | null = null;
                    if (secondary) {
                        const secondaryLayerId = (secondary.layer as FeatureLayer | undefined)?.layerId;
                        const secondaryStatusField = getStatusFieldName(secondary);
                        const secondaryStatus = secondaryStatusField
                            ? secondary.attributes?.[secondaryStatusField]
                            : undefined;
                        if (
                            secondaryLayerId === undefined ||
                            secondaryLayerId === null ||
                            secondaryStatus === undefined
                        ) {
                            return null;
                        }
                        const secondaryDefaultStatus =
                            secondaryStatusField && fastPathSecondaryDefault
                                ? (fastPathSecondaryDefault.attributes?.[secondaryStatusField] as number | undefined)
                                : undefined;
                        secondaryInput = {
                            layerId: secondaryLayerId,
                            versionStatus: secondaryStatus as number,
                            defaultStatus: secondaryDefaultStatus,
                            changes: fastPathSecondaryChanges,
                            geometryChanged: fastPathSecondaryGeometryChanged,
                        };
                    }

                    const evaluation = evaluateFastPath(
                        {
                            layerId: primaryLayerId,
                            versionStatus: primaryStatus as number,
                            defaultStatus: primaryDefaultStatus,
                            changes: fastPathPrimaryChanges,
                            geometryChanged: fastPathPrimaryGeometryChanged,
                        },
                        secondaryInput,
                        {
                            relatedSectorChanged: fastPathRelatedSectorChanged,
                            partnerMissing: fastPathPair.partnerMissing,
                            ...fastPathConfigOptions(),
                        }
                    );

                    if (evaluation.canPost) {
                        // Block the click while any attribute save is in flight on the selected
                        // feature, so a save can't land after the diff is read but before post.
                        const editsInFlight = (appContext?.featureEditsInFlight.value ?? 0) > 0;
                        const disabled = isFastPathBusy || editsInFlight;
                        const label = isFastPathBusy
                            ? "Sender..."
                            : editsInFlight
                              ? "Lagrer endring..."
                              : "Poste til Planroot";
                        return (
                            <ConfirmationPopover
                                disabled={disabled}
                                onConfirm={() => postFastPathToPlanroot(evaluation.promotesToOperative)}
                                confirmText="Poste"
                                title="Bekreft posting til Planroot"
                                warning={evaluation.promotesToOperative}
                                message={
                                    evaluation.promotesToOperative
                                        ? "Objektet har status Prosjektert/Eksisterende i planroot. Endringen sendes til Kartverket, og status settes til Operativ i planroot og versjon. Er du sikker på at du vil poste endringene til Planroot?"
                                        : "Er du sikker på at du vil poste endringene til Planroot?"
                                }
                                onCancel={() => {}}
                                buttonClass="feature-viewer-footer-button"
                            >
                                <div className="nfs-button h-100 break-word">{label}</div>
                            </ConfirmationPopover>
                        );
                    }

                    // Blocked, but a plausible candidate (has pending changes) → info icon.
                    const hasPendingChanges = fastPathPrimaryChanges.length > 0 || fastPathSecondaryChanges.length > 0;
                    if (!hasPendingChanges || evaluation.reasons.length === 0) return null;

                    return (
                        <Popover
                            isOpen={fastPathInfoOpen}
                            positions={["top", "bottom"]}
                            align="end"
                            padding={8}
                            boundaryInset={8}
                            onClickOutside={() => setFastPathInfoOpen(false)}
                            content={
                                <div className="fast-path-info-popover">
                                    <strong>Direkte posting er ikke tilgjengelig:</strong>
                                    <ul>
                                        {evaluation.reasons.map((reason, i) => (
                                            <li key={i}>{fastPathReasonText(reason)}</li>
                                        ))}
                                    </ul>
                                </div>
                            }
                        >
                            <button
                                type="button"
                                className="empty-button"
                                aria-label="Hvorfor kan jeg ikke poste direkte til Planroot?"
                                onMouseEnter={() => setFastPathInfoOpen(true)}
                                onMouseLeave={() => setFastPathInfoOpen(false)}
                                onClick={() => setFastPathInfoOpen((open) => !open)}
                            >
                                <InfoIcon />
                            </button>
                        </Popover>
                    );
                })()}
            </div>

            <ScreeningReportModal
                isScreenReportModalOpen={isScreenReportModalOpen}
                closeModal={() => setIsScreenReportModalOpen(false)}
                selectedFeature={selectedFeature}
                selectedWorkOrderIds={selectedWorkOrderIds}
                setSelectedWorkOrderIds={setSelectedWorkOrderIds}
            />
            <RestoreObjectModal
                isRestoreModalOpen={isRestoreModalOpen}
                closeModal={() => setIsRestoreModalOpen(false)}
                selectedFeature={selectedFeature}
                restorableObjects={restorableObjects}
                isLoadingRestorableObjects={isLoadingRestorableObjects}
            />
        </div>
    );
};

export default FeatureEditor;
